"use client";

import { Text } from "lucide-react";
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  use,
  useEffect,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { readStickyTopPx } from "./_lib/read-sticky-geometry";

export interface StoryTOCItem {
  id: string;
  text: string;
}

const ActiveAnchorContext = createContext<string | null>(null);

export function useActiveAnchorId(): string | null {
  return use(ActiveAnchorContext);
}

export function StoryTOCProvider({
  items,
  children,
}: {
  items: StoryTOCItem[];
  children: ReactNode;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (items.length === 0) {
      return;
    }
    const headings = items
      .map((item) => document.getElementById(item.id))
      .filter((element): element is HTMLElement => element !== null);
    if (headings.length === 0) {
      return;
    }

    const rootMarginTop = readStickyTopPx();
    const visibleIds = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleIds.add(entry.target.id);
          } else {
            visibleIds.delete(entry.target.id);
          }
        }

        for (const item of items) {
          if (visibleIds.has(item.id)) {
            setActiveId(item.id);
            return;
          }
        }

        let fallbackId: string | null = null;
        let minDistance = Number.POSITIVE_INFINITY;
        for (const heading of headings) {
          const rect = heading.getBoundingClientRect();
          if (rect.top > rootMarginTop) {
            continue;
          }
          const distance = rootMarginTop - rect.top;
          if (distance < minDistance) {
            minDistance = distance;
            fallbackId = heading.id;
          }
        }
        if (fallbackId) {
          setActiveId(fallbackId);
        }
      },
      {
        rootMargin: `-${String(rootMarginTop)}px 0px -60% 0px`,
        threshold: 0,
      }
    );

    for (const heading of headings) {
      observer.observe(heading);
    }
    return () => {
      observer.disconnect();
    };
  }, [items]);

  return <ActiveAnchorContext value={activeId}>{children}</ActiveAnchorContext>;
}

function handleAnchorClick(event: React.MouseEvent<HTMLAnchorElement>) {
  const href = event.currentTarget.getAttribute("href");
  if (!href?.startsWith("#")) {
    return;
  }
  const target = document.getElementById(href.slice(1));
  if (!target) {
    return;
  }
  event.preventDefault();
  target.scrollIntoView({ behavior: "smooth", block: "start" });
  history.replaceState(null, "", href);
}

function TOCLink({
  item,
  onItemClick,
}: {
  item: StoryTOCItem;
  onItemClick?: () => void;
}) {
  const activeId = useActiveAnchorId();
  const isActive = activeId === item.id;
  return (
    <a
      className={cn(
        "relative my-0.5 scroll-m-4 rounded-md px-3 py-1.5 pl-4 text-[0.8125rem] text-[var(--explore-text-secondary)] leading-snug no-underline transition-colors first:mt-0 last:mb-0 hover:bg-[var(--explore-bg)] hover:text-[var(--explore-text-primary)]",
        "before:-translate-y-1/2 before:absolute before:top-1/2 before:left-0 before:h-4 before:w-[3px] before:rounded-r-full before:bg-[var(--explore-accent-yellow)] before:opacity-0 before:transition-opacity",
        "data-[active=true]:font-semibold data-[active=true]:text-[var(--explore-text-primary)] data-[active=true]:before:opacity-100"
      )}
      data-active={isActive}
      href={`#${item.id}`}
      onClick={(event) => {
        handleAnchorClick(event);
        onItemClick?.();
      }}
    >
      {item.text}
    </a>
  );
}

export function TOCItemsContainer({
  items,
  onItemClick,
  className,
  ...props
}: {
  items: StoryTOCItem[];
  onItemClick?: () => void;
} & ComponentProps<"div">) {
  return (
    <div className={cn("flex flex-col", className)} {...props}>
      {items.map((item) => (
        <TOCLink item={item} key={item.id} onItemClick={onItemClick} />
      ))}
    </div>
  );
}

export function StoryTOCDesktop({ items }: { items: StoryTOCItem[] }) {
  if (items.length < 2) {
    return null;
  }

  return (
    <nav
      aria-label="Table of contents"
      className="hidden flex-col gap-3 lg:flex"
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      <h3 className="inline-flex items-center gap-1.5 font-medium text-[0.75rem] text-[var(--explore-text-muted)]">
        <Text aria-hidden="true" className="size-3.5" />
        On this story
      </h3>
      <div className="relative max-h-[50vh] min-h-0 overflow-auto py-3 [mask:linear-gradient(to_bottom,transparent,white_16px,white_calc(100%-16px),transparent)] [scrollbar-width:none]">
        <TOCItemsContainer items={items} />
      </div>
    </nav>
  );
}
